import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, IonTextarea, AlertController, LoadingController } from '@ionic/angular/lazy';
import { ObserverService } from 'src/app/services/observer.service';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-organisation-add-comment',
    templateUrl: './organisation-add-comment.page.html',
    styleUrls: ['./organisation-add-comment.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrganisationAddCommentPage implements OnInit {

    @ViewChild('comment_input', { static: false }) comment_input: IonTextarea;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    input_focused = false;
    @Input() organisation_id = null;
    organisation = null;
    comment = null;

    constructor(
        private modalController: ModalController,
        private alertController: AlertController,
        private loadingController: LoadingController,
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        private observerService: ObserverService

    ) { }

    ngOnInit() {

        if(this.organisation_id) {


            this.loadData().then(() => { }).catch(() => { });

        }

        this.observerService.subscribe('network_status_changed|organisation_add_comment_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to add a comment, please check your network connection and try again.");
                }
            }
        });

    }

    // NOTE: focus input after rendering.
    ngAfterViewChecked() {
        if(!this.loading && this.online && !this.input_focused && this.comment_input) {
            setTimeout(() => {
                this.comment_input.setFocus();
                this.input_focused = true;
            }, 100);
        }
    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|organisation_add_comment_modal");
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading organisation").then(() => {
                this.loadOrganisation().then(() => {
                    this.loading_spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    loadOrganisation() {
        return new Promise<void>((resolve, reject) => {
            this.organisation = null;
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrganisation(this.organisation_id).then((organisation: any) => {
                    if(organisation) {
                        this.organisation = organisation;
                    }
                    resolve();
                })
                .catch((error) => {
                    this.organisation = {};
                    if(error.status == 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to retrieve an organisation.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to view an organisation.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve an organisation at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    add() {

        let validated = true;

        if(this.comment == null || this.comment.trim() === "") {
            validated = false;
            this.presentAlert("Missing comment", "Please enter a comment.");
        }

        if(validated) {
            this.addComment().then(() => {
                this.modalController.dismiss();
            });
        }

    }

    addComment() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner('Adding comment').then(() => {
                this.authService.isLoggedIn().then(() => {
                    this.apiService.addOrganisationComment(this.organisation_id, this.comment).then(() => {
                        this.observerService.publish('organisation_comment_added',this.organisation_id);
                        this.spinner.dismiss();
                        resolve();
                    })
                    .catch((error) => {
                        if(error.status === 400) {
                            this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to add a comment.");
                        } 
                        else if(error.status === 403) {
                            this.presentAlert("Insufficient permissions", "You don't have permission to add a comment.");
                        } 
                        else if(error.status == 404) {
                            this.presentAlert("Unavailable", "Unfortunately we're unable to add a comment at this time.");
                        }
                        else if(error.status == 0) {
                            this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                        }
                        this.spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                        window.location.reload();
                    });
                });
            });
        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            cssClass:'alert',
            buttons: ['OK']
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}